import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useMemo, useState } from 'react';
import { times } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';

import {
  useModel,
  useModelCreate,
  useModelDelete,
  useModelIndex,
  useModelKey,
  useModelUpdate,
  useModelKeyIndex
} from '@rhino-project/core/hooks';
import { useRosterStatuses } from '../../hooks/rosterstatus';
import type { RhinoRead } from '@rhino-project/core';

type Lineup = RhinoRead<'lineup'>;

// The board reads less than a whole lineup, which is how the spec has always
// built one, so the shapes state what is read and take their field types from
// the payload.
type BoardGroup = Pick<
  LineupTypeGroup,
  'id' | 'display_name' | 'default_count'
> & {
  position?: Partial<LineupTypeGroup['position']> | null;
  children?: BoardGroup[] | null;
};

type BoardRoster = Pick<Roster, 'id' | 'number'> & {
  player: Partial<Roster['player']>;
};

type BoardSpot = Pick<LineupSpot, 'id' | 'group_index'> & {
  roster: BoardRoster;
};

type LineupBoard = Pick<Lineup, 'id'> & {
  team_season: Partial<Lineup['team_season']>;
  lineup_type: { lineup_type_groups: BoardGroup[] };
  lineup_spots: BoardSpot[];
};
type LineupSpot = Lineup['lineup_spots'][number];
type LineupTypeGroup = Lineup['lineup_type']['lineup_type_groups'][number];
type Roster = LineupSpot['roster'];

type DropHandler = (sourceId: string, destId: string) => void;
type DragStateHandler = (dragging: boolean) => void;

interface DropTargetResult {
  isOver: boolean;
  onDragOver: (e: React.DragEvent<HTMLElement>) => void;
  onDragEnter: (e: React.DragEvent<HTMLElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLElement>) => void;
  onDrop: (e: React.DragEvent<HTMLElement>) => void;
  'data-droppable-id': string;
}

const DRAG_MIME = 'application/x-lineup-drag';

// --- Drag helpers ---
const useDragSource = (
  droppableId: string,
  onDragStateChange?: DragStateHandler
) => {
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.dataTransfer.setData(DRAG_MIME, droppableId);
      e.dataTransfer.effectAllowed = 'move';
      onDragStateChange?.(true);
    },
    [droppableId, onDragStateChange]
  );

  const handleDragEnd = useCallback(() => {
    onDragStateChange?.(false);
  }, [onDragStateChange]);

  return {
    draggable: true as const,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd
  };
};

const useDropTarget = (
  droppableId: string,
  onDrop: DropHandler
): DropTargetResult => {
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      setIsOver(false);
      const sourceId = e.dataTransfer.getData(DRAG_MIME);
      if (sourceId && sourceId !== droppableId) {
        onDrop(sourceId, droppableId);
      }
    },
    [droppableId, onDrop]
  );

  return {
    isOver,
    onDragOver: handleDragOver,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    'data-droppable-id': droppableId
  };
};

// --- Layout helpers ---
const rosterByPath = <S extends { group_index: string }>(
  spots: S[],
  path: string
) => spots.filter((s) => s.group_index === path);

const extendPath = (base_path: string, group: { id: number }) =>
  base_path ? `${base_path}/${group.id}` : `${group.id}`;

const extendPathForIndex = (
  base_path: string,
  group: { id: number },
  index: number
) => `${extendPath(base_path, group)}/${index}`;

const rostersNotInLineup = (
  lineupSpots: BoardSpot[],
  rosterSpots: BoardRoster[]
) => {
  const inLineupIds = lineupSpots.map((ls) => ls.roster.id);
  return rosterSpots.filter((rs) => !inLineupIds.includes(rs.id));
};

// --- Components ---

interface EmptySpotProps {
  lineup_type_group: BoardGroup;
  dropProps: DropTargetResult;
}

const EmptySpot = ({ lineup_type_group, dropProps }: EmptySpotProps) => {
  const { isOver, ...domDropProps } = dropProps;
  const lineupPosition = lineup_type_group.position?.display_name;
  return (
    <div
      {...domDropProps}
      className={`rounded border border-dashed border-separator text-muted text-sm px-3 py-1 text-center w-[160px] transition-colors ${
        isOver ? 'bg-surface-secondary border-accent text-accent' : ''
      }`}
    >
      {lineupPosition}
    </div>
  );
};

interface PlayerCardProps {
  player: Partial<Roster['player']>;
  number?: string;
}

const PlayerCard = ({ player, number }: PlayerCardProps) => {
  const playerHand = player.hand || '-';
  const playerNumber = number || '-';

  return (
    <div className="flex items-center rounded border border-border-secondary bg-surface-secondary shadow-surface w-[160px]">
      <span className="px-2 py-1 text-xs font-mono w-[28px] text-center border-r border-border-secondary shrink-0">
        {playerNumber}
      </span>
      <span className="px-2 py-1 text-sm flex-1 truncate">
        {player.last_name as string}
      </span>
      <span className="px-2 py-1 text-xs font-mono w-[24px] text-center border-l border-border-secondary shrink-0">
        {playerHand}
      </span>
    </div>
  );
};

interface RosterCardProps {
  roster: BoardRoster;
  dragProps?: ReturnType<typeof useDragSource>;
  dropProps?: DropTargetResult;
}

const RosterCard = ({ roster, dragProps, dropProps }: RosterCardProps) => {
  const { isOver, ...domDropProps } =
    dropProps || ({} as Partial<DropTargetResult>);
  const player = roster.player;
  return (
    <div
      {...dragProps}
      {...domDropProps}
      className={`cursor-grab active:cursor-grabbing transition-colors w-[160px] ${
        isOver ? 'ring-2 ring-accent rounded' : ''
      }`}
    >
      <PlayerCard
        player={player}
        number={(roster.number as string) || (player.number as string)}
      />
    </div>
  );
};

interface LineupSpotCardProps {
  lineup_spot: BoardSpot;
  path: string;
  onDrop: DropHandler;
  onRemove?: (id: unknown) => void;
  isDragging: boolean;
  onDragStateChange: DragStateHandler;
}

const LineupSpotCard = ({
  lineup_spot,
  path,
  onDrop,
  onRemove,
  isDragging,
  onDragStateChange
}: LineupSpotCardProps) => {
  const droppableId = `ls-${lineup_spot.id}-${path}`;
  const dragProps = useDragSource(droppableId, onDragStateChange);
  const dropProps = useDropTarget(droppableId, onDrop);
  const { isOver, ...domDropProps } = dropProps;
  const roster = lineup_spot.roster;
  const player = roster.player;

  return (
    <div
      className={`relative group w-[160px] ${
        isOver ? 'ring-2 ring-accent' : ''
      }`}
    >
      <div
        {...dragProps}
        {...domDropProps}
        className="cursor-grab active:cursor-grabbing"
      >
        <PlayerCard
          player={player}
          number={(roster.number as string) || (player.number as string)}
        />
      </div>
      {onRemove && !isDragging && (
        <button
          type="button"
          onClick={() => onRemove(lineup_spot.id)}
          className="absolute -top-1.5 -right-1.5 hidden group-hover:flex w-5 h-5 items-center justify-center rounded-full bg-danger text-danger-foreground text-xs leading-none shadow-surface"
        >
          x
        </button>
      )}
    </div>
  );
};

interface ReserveCardProps {
  roster: BoardRoster;
  onDrop: DropHandler;
  onDragStateChange: DragStateHandler;
}

const ReserveCard = ({
  roster,
  onDrop,
  onDragStateChange
}: ReserveCardProps) => {
  const droppableId = `rs-${roster.id}`;
  const dragProps = useDragSource(droppableId, onDragStateChange);
  const dropProps = useDropTarget(droppableId, onDrop);

  return (
    <RosterCard roster={roster} dragProps={dragProps} dropProps={dropProps} />
  );
};

interface DroppableEmptySpotProps {
  lineup_type_group: BoardGroup;
  path: string;
  onDrop: DropHandler;
}

const DroppableEmptySpot = ({
  lineup_type_group,
  path,
  onDrop
}: DroppableEmptySpotProps) => {
  const droppableId = `lg-${lineup_type_group.id}-${path}`;
  const dropProps = useDropTarget(droppableId, onDrop);

  return (
    <EmptySpot lineup_type_group={lineup_type_group} dropProps={dropProps} />
  );
};

interface LineupTypeGroupsProps {
  level?: number;
  base_path?: string;
  groups: BoardGroup[];
  spots: BoardSpot[];
  row?: boolean;
  onDrop: DropHandler;
  onRemove?: (id: unknown) => void;
  isDragging: boolean;
  onDragStateChange: DragStateHandler;
}

const LineupTypeGroups = ({
  level = 1,
  base_path = '',
  groups,
  spots,
  row,
  onDrop,
  onRemove,
  isDragging,
  onDragStateChange
}: LineupTypeGroupsProps) => {
  return (
    <div
      className={`flex ${
        row ? 'flex-row justify-center' : 'flex-col'
      } flex-1 items-center gap-2 p-1`}
    >
      {groups.map((g) => {
        const isLeaf = !g.children;
        const defaultCount = g.default_count as number;
        // For parent groups (lines, pairings) allow growing past default_count
        // so a coach can drop a 5th forward line / 4th D pair onto an empty
        // extra slot. Leaves stay capped at their default count.
        let slots = defaultCount;
        if (!isLeaf) {
          const groupPrefix = extendPath(base_path, g);
          let maxFilled = -1;
          (spots || []).forEach((s) => {
            const gi = s.group_index as string | undefined;
            if (gi && gi.startsWith(`${groupPrefix}/`)) {
              const next = Number(
                gi.slice(groupPrefix.length + 1).split('/')[0]
              );
              if (!Number.isNaN(next) && next > maxFilled) maxFilled = next;
            }
          });
          slots = Math.max(defaultCount, maxFilled + 2);
        }
        return (
          <div key={g.id} className="contents">
            {level === 1 && (
              <div className="w-full text-center text-xs font-semibold uppercase tracking-wider text-muted mt-2 first:mt-0">
                {String(g.position?.display_name || g.display_name)}
              </div>
            )}
            {times(slots, (t) => {
              const path = extendPathForIndex(base_path, g, t);
              const rosterSpots = rosterByPath(spots || [], path);
              return (
                <div key={`${g.id}-${t}`} className="contents">
                  {rosterSpots.map((ls) => (
                    <LineupSpotCard
                      key={`ls-${ls.id}`}
                      lineup_spot={ls}
                      path={path}
                      onDrop={onDrop}
                      onRemove={onRemove}
                      isDragging={isDragging}
                      onDragStateChange={onDragStateChange}
                    />
                  ))}
                  {isLeaf &&
                    times(
                      Math.max(defaultCount - rosterSpots.length, 0),
                      (i) => (
                        <DroppableEmptySpot
                          key={`empty-${g.id}-${t}-${i}`}
                          lineup_type_group={g}
                          path={path}
                          onDrop={onDrop}
                        />
                      )
                    )}
                  {!isLeaf && (
                    <LineupTypeGroups
                      level={level + 1}
                      base_path={path}
                      groups={g.children!}
                      spots={spots}
                      row={!row}
                      onDrop={onDrop}
                      onRemove={onRemove}
                      isDragging={isDragging}
                      onDragStateChange={onDragStateChange}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

interface LineupProps {
  lineup: LineupBoard;
}

const Lineup = ({ lineup }: LineupProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const team_season = useMemo(() => lineup?.team_season, [lineup]);
  const lineupSpotModel = useModel('lineup_spot');
  const lineupIndexKey = useModelKeyIndex('lineup');
  const lineupShowRootKey = useModelKey('lineup', 'show');

  const { resources: rosterStatuses } = useRosterStatuses({
    status_type: team_season?.status
  });

  const filter = useMemo(() => {
    const results = rosterStatuses?.results;
    const statuses =
      team_season?.status === 'planning'
        ? (results?.slice(-2) || [])?.map((rs) => rs.name)
        : [];

    return {
      team_season_id: team_season?.id,
      roster_status: {
        status_type: team_season?.status,
        name: statuses
      }
    };
  }, [team_season, rosterStatuses]);

  // Alphabetical — the reserve pool is scanned for a specific player to drag
  // into a line, not read as a ranking.
  const { resources: rosters } = useModelIndex('roster', {
    filter,
    order: ['player.last_name', 'player.first_name'],
    limit: 10000,
    queryOptions: { enabled: !!rosterStatuses }
  });

  const { mutate: lineupSpotUpdate } = useModelUpdate(lineupSpotModel);
  const { mutate: lineupSpotCreate } = useModelCreate(lineupSpotModel);
  const { mutate: lineupSpotDelete } = useModelDelete(lineupSpotModel);

  const refreshLineup = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: lineupIndexKey });
    // Invalidate every lineup show query, not just this lineup's: PP / PK
    // children render from data sideloaded on the parent's show payload,
    // so mutating a child must also bust the parent's cache.
    queryClient.invalidateQueries({ queryKey: lineupShowRootKey });
  }, [queryClient, lineupIndexKey, lineupShowRootKey]);

  const handleDrop = useCallback(
    (sourceDroppableId: string, destDroppableId: string) => {
      // Parse source and destination IDs
      // Format: {type}-{id}[-{path}] where type is rs, ls, lg, or scratches
      const parseDndId = (id: string) => {
        const dashIdx = id.indexOf('-');
        if (dashIdx === -1)
          return {
            type: id,
            id: null as string | null,
            path: null as string | null
          };
        const type = id.substring(0, dashIdx);
        const rest = id.substring(dashIdx + 1);
        // For ls and lg, format is type-id-path where path can contain dashes
        const secondDash = rest.indexOf('-');
        if (secondDash === -1)
          return { type, id: rest, path: null as string | null };
        return {
          type,
          id: rest.substring(0, secondDash),
          path: rest.substring(secondDash + 1)
        };
      };

      const source = parseDndId(sourceDroppableId);
      const dest = parseDndId(destDroppableId);

      if (source.type === 'rs') {
        // Dragging from reserves
        if (dest.type === 'lg') {
          // Drop to empty lineup group -> create lineup_spot
          const ids = dest.path!.split('/');
          lineupSpotCreate(
            {
              lineup: lineup.id,
              lineup_group: ids[ids.length - 2],
              lineup_type_group: ids[ids.length - 2],
              roster: source.id,
              group_index: dest.path
            } as unknown as Parameters<typeof lineupSpotCreate>[0],
            { onSuccess: refreshLineup }
          );
        } else {
          // Drop to existing lineup spot -> update roster
          lineupSpotUpdate(
            { id: dest.id, roster: source.id } as unknown as Parameters<
              typeof lineupSpotUpdate
            >[0],
            { onSuccess: refreshLineup }
          );
        }
      } else if (dest.type === 'lg') {
        // Drop from lineup position to blank group
        lineupSpotUpdate(
          { id: source.id, group_index: dest.path } as unknown as Parameters<
            typeof lineupSpotUpdate
          >[0],
          { onSuccess: refreshLineup }
        );
      } else if (dest.type === 'scratches') {
        // Drop to scratches -> delete lineup_spot
        lineupSpotDelete(source.id!, { onSuccess: refreshLineup });
      } else {
        // Swap two lineup spots
        lineupSpotUpdate(
          { id: dest.id, group_index: source.path } as unknown as Parameters<
            typeof lineupSpotUpdate
          >[0],
          {
            onSuccess: () =>
              lineupSpotUpdate(
                {
                  id: source.id,
                  group_index: dest.path
                } as unknown as Parameters<typeof lineupSpotUpdate>[0],
                { onSuccess: refreshLineup }
              )
          }
        );
      }
    },
    [
      lineup,
      lineupSpotCreate,
      lineupSpotUpdate,
      lineupSpotDelete,
      refreshLineup
    ]
  );

  const handleRemove = useCallback(
    (lineupSpotId: unknown) => {
      lineupSpotDelete(lineupSpotId as string, { onSuccess: refreshLineup });
    },
    [lineupSpotDelete, refreshLineup]
  );

  const [isDragging, setIsDragging] = useState(false);
  const handleDragStateChange = useCallback(
    (dragging: boolean) => setIsDragging(dragging),
    []
  );

  const scratchesDropProps = useDropTarget('scratches', handleDrop);
  const { isOver: scratchesIsOver, ...scratchesDomProps } = scratchesDropProps;

  const lineupType = lineup.lineup_type;
  const lineupSpots = lineup.lineup_spots;

  return (
    <div>
      <div className="py-4">
        <LineupTypeGroups
          groups={lineupType.lineup_type_groups}
          spots={lineupSpots || []}
          onDrop={handleDrop}
          onRemove={handleRemove}
          isDragging={isDragging}
          onDragStateChange={handleDragStateChange}
        />
      </div>
      <div className="mt-4 border-t border-separator pt-4">
        <div className="flex flex-wrap gap-2 items-start">
          <div
            {...scratchesDomProps}
            className={`rounded border border-dashed border-separator px-3 py-1 text-sm italic transition-colors min-h-[32px] ${
              scratchesIsOver ? 'bg-danger/10 border-danger' : ''
            }`}
          >
            {t('frontend.messages.remove')}
          </div>
          {rosters &&
            rostersNotInLineup(lineupSpots || [], rosters.results).map((rs) => (
              <ReserveCard
                key={`rs-${rs.id}`}
                roster={rs}
                onDrop={handleDrop}
                onDragStateChange={handleDragStateChange}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default Lineup;
